import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../../api';
import { sectorSchema } from '../../schemas';
import { toast } from '../../utils/toast';
import { ModernBadge, ModernButton, ModernCard, ModernInput } from '../ui/modern';
import Modal from '../ui/Modal';

const defaultValues = {
  name: '',
  code: '',
  description: '',
  isActive: true,
  isDefault: false,
};

export default function SectorsManager() {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // sector row or null
  const [modalOpen, setModalOpen] = useState(false);

  const formSchema = useMemo(() => sectorSchema, []);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  async function fetchSectors() {
    setLoading(true);
    try {
      const res = await api.get('/admin/sectors');
      setSectors(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load sectors');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSectors();
  }, []);

  function openCreate() {
    setEditing(null);
    reset(defaultValues);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    reset({
      name: row.name || '',
      code: row.code || '',
      description: row.description || '',
      isActive: !!row.isActive,
      isDefault: !!row.isDefault,
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      ...values,
      description: values.description?.trim() ? values.description.trim() : null,
    };
    try {
      if (editing?.id) {
        await api.put(`/admin/sectors/${editing.id}`, payload);
        toast.success('Sector updated');
      } else {
        await api.post('/admin/sectors', payload);
        toast.success('Sector created');
      }
      setModalOpen(false);
      await fetchSectors();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to save sector');
    }
  }

  async function disableSector(row) {
    if (row.isDefault) {
      toast.error('Cannot disable the default sector');
      return;
    }
    if (!window.confirm(`Disable sector "${row.name}"?`)) return;
    try {
      await api.delete(`/admin/sectors/${row.id}`);
      toast.success('Sector disabled');
      await fetchSectors();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to disable sector');
    }
  }

  return (
    <ModernCard>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Sectors</h2>
          <p className="text-gray-600 dark:text-slate-400 text-sm">Create and manage sector catalogs</p>
        </div>
        <ModernButton variant="primary" onClick={openCreate}>
          + Add Sector
        </ModernButton>
      </div>

      {loading ? (
        <div className="text-gray-700 dark:text-slate-300">Loading…</div>
      ) : sectors.length === 0 ? (
        <div className="text-gray-700 dark:text-slate-300">No sectors found.</div>
      ) : (
        <>
          <div className="md:hidden space-y-3">
            {sectors.map((s) => (
              <div key={s.id} className="border border-gray-200 dark:border-white/10 rounded-xl p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-gray-900 dark:text-white font-semibold">{s.name}</div>
                    <div className="text-xs text-gray-600 dark:text-slate-400">{s.code}</div>
                  </div>
                  <ModernBadge variant={s.isActive ? 'success' : 'secondary'}>
                    {s.isActive ? 'Active' : 'Disabled'}
                  </ModernBadge>
                </div>
                <div className="text-sm text-gray-600 dark:text-slate-400">
                  Default: <span className="text-gray-900 dark:text-white">{s.isDefault ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <ModernButton variant="glass" onClick={() => openEdit(s)}>
                    Edit
                  </ModernButton>
                  <ModernButton
                    variant="danger"
                    onClick={() => disableSector(s)}
                    disabled={s.isDefault}
                  >
                    Disable
                  </ModernButton>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
            <thead>
              <tr className="text-gray-600 dark:text-slate-400 text-sm">
                <th className="py-2">Name</th>
                <th className="py-2">Code</th>
                <th className="py-2">Status</th>
                <th className="py-2">Default</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sectors.map((s) => (
                <tr key={s.id} className="border-t border-gray-200 dark:border-white/10">
                  <td className="py-3 text-gray-900 dark:text-white font-medium">{s.name}</td>
                  <td className="py-3 text-gray-700 dark:text-slate-300">{s.code}</td>
                  <td className="py-3">
                    <ModernBadge variant={s.isActive ? 'success' : 'secondary'}>
                      {s.isActive ? 'Active' : 'Disabled'}
                    </ModernBadge>
                  </td>
                  <td className="py-3 text-gray-700 dark:text-slate-300">{s.isDefault ? '✓' : '—'}</td>
                  <td className="py-3 text-right space-x-2">
                    <ModernButton variant="glass" onClick={() => openEdit(s)}>
                      Edit
                    </ModernButton>
                    <ModernButton
                      variant="danger"
                      onClick={() => disableSector(s)}
                      disabled={s.isDefault}
                    >
                      Disable
                    </ModernButton>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Sector' : 'Add Sector'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <ModernInput
            label="Name"
            placeholder="e.g., Accounting"
            error={errors.name?.message}
            {...register('name')}
          />
          <ModernInput
            label="Code"
            placeholder="e.g., accounting"
            error={errors.code?.message}
            {...register('code')}
          />
          <ModernInput
            label="Description (optional)"
            placeholder="Short description"
            error={errors.description?.message}
            {...register('description')}
          />

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-gray-700 dark:text-slate-200 text-sm">
              <input type="checkbox" {...register('isActive')} />
              Active
            </label>
            <label className="flex items-center gap-2 text-gray-700 dark:text-slate-200 text-sm">
              <input type="checkbox" {...register('isDefault')} />
              Default sector
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <ModernButton variant="glass" type="button" onClick={() => setModalOpen(false)}>
              Cancel
            </ModernButton>
            <ModernButton variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save'}
            </ModernButton>
          </div>
        </form>
      </Modal>
    </ModernCard>
  );
}


